/* global nv, NO_DATA_AVAILABLE*/
import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { useScreenSize } from "common/hooks";
import { translateCoordsOver } from "common/graphs/NVLineChart";
import { useDimensions } from "common/hooks";
import { mayExpandLinearScale } from "./graphs/NVLineChart";

const WrapperDIV = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 100%;
    height: 100%;
    .nv-background rect {
      stroke: var(--chart-inner-border-color, #000);
      stroke-opacity: 0.75;
    }
    .nvd3 .nv-axis path.domain {
      stroke-opacity: 0;
    }
    text.threshold-label {
      text-anchor: end;
      dominant-baseline: text-after-edge;
    }
    .nv-axislabel {
      transform: translate(10px, 0) rotate(-90deg);
    }
    --graph-min-height: ${(props) => props.minHeight || "6cm"};
    height: calc(${(props) => 0}px + var(--graph-min-height)) !important;

    .nv-interactive {
      display: none;
    }
  }
`;

const limitY = (value, min, max) => {
  if (value === null || isNaN(value) || value === undefined) {
    return value;
  }
  if (min !== null && max !== null) {
    value = value < min ? min : value;
    value = value > max ? max : value;
    return value;
  }

  if (min === null && max !== null) {
    return value > max ? max : value;
  }

  if (min !== null && max === null) {
    return value < min ? min : value;
  }

  return value;
};

const splitByFields = (
  [xField, ...fields],
  items,
  range = [null, null]
) => {
  const lanes = fields.map(
    ({ name, label, disabled, color = undefined, area }) => {
      const values = items.map((item, index) => {
        return [item[xField], limitY(item[name], range[0], range[1])];
      });
      return {
        key: label,
        disabled,
        values,
        ...(color === undefined ? {} : { color }),
        area,
      };
    }
  );
  return [lanes, range];
};

let bestOfBest = 0;

const calcItemsRatio = (count, width) => {
  return (count > width && Math.floor(count / (width * 0.4))) || 1;
};

const NVLineChartLive = ({
  margin = { top: 90, right: 50, bottom: 30, left: 70 },
  items,
  fields,
  onHighlight,
  highlight,
  minHeight,
  onRange,
  renderRate = 1,
  yAxisUnits,
  xAxisFormat,
  yAxisFormat,
  className,
  addSerieNameToClass=false,
  fixedYRange=[0, null],
}) => {
  const [wrapper, canvas] = [useRef(null), useRef(null)];
  const {width} = useDimensions(wrapper);
  const isLoading = useRef(true);
  const lastRenderAt = useRef();
  const [range, setRange] = useState(0);
  let chart = null;
  const xField = "time";

  useEffect(() => {
    if(renderRate && (isNaN(renderRate) || Number(renderRate) < 0)){
      throw new Error("renderRate must be a non-negative number");
    }
  }, [renderRate]);

  const render = (target) => {
    isLoading.current = true;
    const now = Date.now();

    if (
      document.visibilityState === "hidden" ||
      renderRate === 0 ||
      (lastRenderAt.current && now - lastRenderAt.current < renderRate * 1000)
    ) {
      return;
    }

    lastRenderAt.current = now;

    nv.addGraph(function () {
      if (chart === null) {
        chart = nv.models
          .lineChart()
          .margin(margin)
          .x((d) => d[0])
          .y((d) => d[1])
          .useInteractiveGuideline(true) // This affects tooltip behavior
          .noData(NO_DATA_AVAILABLE)
          .duration(0)
          .yScale(
            mayExpandLinearScale(d3.scaleLinear(), {
              topGap: 0.05,
            })
          )
          .xScale(d3.scaleTime())
          .showXAxis(true)
          .showLegend(false);
        chart.interactiveLayer.tooltip.enabled(false);
        chart.xAxis.tickFormat(xAxisFormat);
        chart.yAxis
          .axisLabel(yAxisUnits)
          .showMaxMin(false)
          .tickFormat(yAxisFormat);
      }
      const [datum, [yMin, yMax]] = splitByFields(
        [xField, ...fields],
        items,
        fixedYRange
      );
      d3.select(canvas.current).datum(
        datum.map((d) => {
          return { ...d, class: d.key };
        })
      );

      const main = d3.select(canvas.current);
      const rangeWidthNew = width - margin.left - margin.right;
      if (rangeWidthNew !== range) {
        setRange(rangeWidthNew);
      }

      main.select(".nv-background rect").attr("rx", 9);

      chart.dispatch.on("renderEnd", function () {
        isLoading.current = false;
        const linesArea = main.select(".nv-groups");
        if (addSerieNameToClass) {
          const groups = linesArea.selectAll(".nv-group");
          groups.each(function (d, i) {
            d3.select(this).attr(
              "class",
              `${d.class} nv-group nv-series-${i}`
            );
          });
        }
      });
      if (onHighlight && onHighlight !== null) {
        const translateCoords = translateCoordsOver(main.node());
        chart.interactiveLayer.dispatch.on("elementMousemove.name", (event) => {
          if (event.synthetic === true) {
            return;
          }
          const { pointXValue, mouseX } = event;
          onHighlight({ synthetic: true, ...translateCoords.out(event) });
        });
        chart.interactiveLayer.dispatch.on("elementMouseout.name", () => {
          onHighlight(null);
        });
      }
      if (highlight && highlight !== null) {
        chart.interactiveLayer.dispatch.elementMousemove(highlight);
      } else {
        chart.interactiveLayer.dispatch.elementMouseout({
          mouseX: 0,
          mouseY: 0,
        });
      }
      
      if (fixedYRange) {
        chart.forceY(fixedYRange);
      }
      d3.select(canvas.current).call(chart);

      return chart;
    });
  };

  useEffect(() => {
    if (onRange) {
      onRange(range);
    }
  }, [range]);

  const timeMark = items.map((item) => new Date(item.time).getSeconds()).join();

  useEffect(() => {
    const target = wrapper.current;
    return items ? render(target) : () => {};
  }, [timeMark, fields, width, fixedYRange]);

  const str = highlight ? new Date(highlight.pointXValue).toISOString() : "";

  useEffect(() => {
    const target = wrapper.current;
    return items ? render(target) : () => {};
  }, [str]);

  return (
    <WrapperDIV ref={wrapper} minHeight={minHeight} className={className}>
      <svg ref={canvas}></svg>
    </WrapperDIV>
  );
};

export default NVLineChartLive;
